import { useQueryClient } from '@tanstack/react-query';
import {
  getListOrganizationWebhooksQueryKey,
  useUpdateOrganizationWebhook,
  type OrganizationWebhookDto,
} from '@sara/api-client';
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@sara/ui/components/dialog';
import type { DialogBaseProps } from '@sara/ui/components/dialog-provider';
import { toast } from '@sara/ui/components/sonner';
import {
  EditWebhookForm,
  toUpdatePatch,
  type EditWebhookFormValues,
} from '../../components/EditWebhookForm';

interface EditWebhookDialogProps extends DialogBaseProps {
  orgId: string;
  webhook: OrganizationWebhookDto;
}

function EditWebhookDialog({ orgId, webhook, onClose }: EditWebhookDialogProps) {
  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useUpdateOrganizationWebhook({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getListOrganizationWebhooksQueryKey({ orgId }),
        });
        toast.success('Webhook updated');
        onClose();
      },
    },
  });

  const initial = { url: webhook.url, label: webhook.label ?? null };

  function handleSubmit(values: EditWebhookFormValues) {
    const patch = toUpdatePatch(initial, values);
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    mutate({ id: webhook.id, data: patch });
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl font-medium tracking-tight">
          Edit webhook
        </DialogTitle>
        <DialogDescription>
          Changes apply to future deliveries. Past deliveries are unaffected.
        </DialogDescription>
      </DialogHeader>
      <EditWebhookForm
        initial={initial}
        onSubmit={handleSubmit}
        onCancel={onClose}
        isPending={isPending}
        error={error instanceof Error ? error.message : error ? 'Failed to update webhook' : null}
      />
    </DialogContent>
  );
}

export { EditWebhookDialog };
